import { motion, useReducedMotion } from 'framer-motion';
import { EASE } from './constants';

interface EditorialNoteSectionProps {
    note: string;
}

/**
 * "From the Editors" callout — editor-curated long-form commentary that
 * sits between the factual Overview and the Fees section. Carries the
 * cognac accent (#7C4D2E) used elsewhere for editorial moments over
 * light surfaces (mini-titles, callouts, tags).
 *
 * Different in voice from `editorial_lede`: the lede is AI-generated
 * editorial framing; the note is hand-written editor commentary. Both
 * live in the CMS zone, never written by ingest.
 */
export default function EditorialNoteSection({ note }: EditorialNoteSectionProps) {
    const shouldReduceMotion = useReducedMotion();

    /* Split on blank lines to preserve paragraph breaks */
    const paragraphs = note.split(/\n\n+/).filter(Boolean);
    if (paragraphs.length === 0) return null;

    return (
        <section className="bg-[#fdfaf3] lux-space-md">
            <div className="lux-space-px mx-auto max-w-3xl">
                <motion.div
                    initial={shouldReduceMotion ? undefined : { opacity: 0, y: 16 }}
                    whileInView={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="border-l-2 border-[#7C4D2E] pl-6 lg:pl-8"
                >
                    <span className="lux-caps lux-caps-sm text-[#7C4D2E]">From the Editors</span>

                    <div className="mt-4 space-y-4">
                        {paragraphs.map((para, i) => (
                            <p key={i} className="lux-didot text-xl leading-relaxed text-[#0e2727]/85 italic md:text-2xl">
                                {para}
                            </p>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
